import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts';
import {
  IconChevronLeft,
  IconChevronRight,
  IconLoader2,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { toast } from 'react-toastify';
import useTheme from 'chat-ui/src/hooks/useTheme';
import dayjs from 'dayjs';
import { FormEvent, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import useTranslation from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useMutateAiSearchAskQuestion, useMutateDeleteAskedQuestion } from '@/reactQuery/post';
import AISearchConversationTable from './ai_search_content/AISearchConversationTable';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import { SearchResult } from '@/types';
import Markdown from '@/components/markdown/Markdown';
import { useGetAISearchResults } from '@/reactQuery/get';

function AISearch() {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [inputQuestion, setInputQuestion] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const questions_pagination = searchParams.get(SearchParam.AI_SEARCH_QUESTION_PAGE);
  const question_params_id = searchParams.get(SearchParam.AI_SEARCH_QUESTION);
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const conversationSelected = searchParams.get(SearchParam.SELECTED_TRANSCRIPT);
  const [currentPage, setCurrentPage] = useState<number>(Number(questions_pagination || 1));
  const { currentTheme } = useTheme();

  const { data: AISearchResults } = useGetAISearchResults(projectId, filterId);

  const { mutateAsync: mutateAsyncSearchAskQuestion, isPending } = useMutateAiSearchAskQuestion(
    filterId,
    conversationSelected,
    AISearchResults,
  );

  const { mutateAsync: mutateAsyncDeleteQuestion } = useMutateDeleteAskedQuestion();
  const AIResults = AISearchResults?.searches || [];
  const totalAIResults = AIResults.length || 0;

  const indexOfLastItem = currentPage * 10;
  const indexOfFirstItem = indexOfLastItem - 10;

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const paginate = (pageNumber: number) => {
    addQueryParams({ [SearchParam.AI_SEARCH_QUESTION_PAGE]: String(pageNumber) });
    setCurrentPage(pageNumber);
  };

  const handleDeleteAskedQuestion = async (searchId: string) => {
    try {
      await mutateAsyncDeleteQuestion({ projectId, searchId, filterId });
      toast.success(lang.get('msg.questionSuccessfullyDeleted'));
    } catch (err) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  const handleAskingQuestion = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (inputQuestion.trim() !== '') {
        await mutateAsyncSearchAskQuestion({ project_id: projectId, inputQuestion });
        setInputQuestion('');
      }
    } catch (err) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  const selectedQuestionAnswer: SearchResult | undefined = question_params_id
    ? AIResults.find((item: { _id: string }) => item._id === question_params_id)
    : AIResults?.[0];

  return (
    <div
      style={{ backgroundColor: currentTheme?.background }}
      className="flex flex-col w-full mx-auto"
    >
      {/* search field */}
      <form className="flex w-full" onSubmit={handleAskingQuestion}>
        <Input
          icon={<IconSearch size={22} />}
          className="rounded-r-none"
          placeholder={lang.get('msg.askYourQuestionHere')}
          value={inputQuestion || ''}
          onChange={(e) => setInputQuestion(e.target.value)}
          disabled={isPending}
        />
        <Button variant="default" className="rounded-l-none" type="submit" disabled={isPending}>
          {isPending && <IconLoader2 className="mr-2 animate-spin" size={18} />}
          {lang.get('msg.search')}
        </Button>
      </form>

      {/* history & responses */}
      {totalAIResults > 0 && (
        <div className="flex w-full gap-4 mt-7 md900:flex-col">
          {/* history */}
          <div className="flex flex-col w-full border divide-y rounded-md shadow h-fit max-w-96 md900:max-w-full">
            {AIResults?.slice(indexOfFirstItem, indexOfLastItem).map((item: SearchResult) => (
              <TooltipWrapper text={item?.question} className="w-full max-w-96" key={item?._id}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() =>
                    addQueryParams({
                      ai_search_question: item?._id,
                      ai_search_question_page: String(currentPage),
                    })
                  }
                  style={{
                    backgroundColor:
                      item?._id === question_params_id || selectedQuestionAnswer?._id === item?._id
                        ? currentTheme?.['lighter-primary']
                        : '',
                  }}
                  className={cn(
                    'relative flex flex-col text-start w-full gap-1 px-6 py-4 cursor-pointer group',
                  )}
                >
                  <p className="text-sm font-semibold line-clamp-2">{item?.question}</p>
                  <p className="text-xs font-medium">
                    {dayjs(item?.created_at).format('DD/MM/YY')}
                  </p>
                  {/* delete question form history */}
                  <button
                    type="button"
                    onClick={() => handleDeleteAskedQuestion(item?._id)}
                    className="absolute group-hover:flex hidden bg-red-500 text-white rounded-full p-1 right-2 bottom-2 hover:scale-[1.1] transition-all ease-in-out"
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
              </TooltipWrapper>
            ))}

            {/* history pagination */}
            <div className="p-1">
              <div className="flex items-center justify-between w-full">
                <Button
                  variant="ghost"
                  className="cursor-pointer select-none"
                  onClick={() => {
                    if (currentPage > 1) {
                      paginate(currentPage - 1);
                    }
                  }}
                  disabled={currentPage === 1}
                >
                  <IconChevronLeft className="mr-1 pt-0.5" size={18} />
                  {lang.get('msg.previous')}
                </Button>

                <Button
                  variant="ghost"
                  type="button"
                  className="flex items-center cursor-pointer select-none"
                  onClick={() => {
                    if (
                      totalAIResults &&
                      totalAIResults >= 10 &&
                      indexOfLastItem < totalAIResults
                    ) {
                      paginate(currentPage + 1);
                    }
                  }}
                  disabled={
                    !totalAIResults || totalAIResults < 10 || indexOfLastItem >= totalAIResults
                  }
                >
                  {lang.get('msg.next')}
                  <IconChevronRight className="ml-1 pt-0.5" size={18} />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col w-full">
            {/* answer */}
            <div
              style={{ backgroundColor: currentTheme?.['lighter-primary'] }}
              className="relative flex flex-col w-full p-6 rounded-md shadow"
            >
              <div className="flex justify-between w-full mb-4">
                <div className="flex flex-col w-full">
                  <p className="font-semibold mb-1">{lang.get('msg.question')}:</p>

                  <p className="text-sm font-medium">{selectedQuestionAnswer?.question}</p>
                </div>
                {/* delete question */}
                {selectedQuestionAnswer && (
                  <button
                    onClick={() => handleDeleteAskedQuestion(selectedQuestionAnswer?._id)}
                    type="button"
                    className="bg-red-500 hover:scale-[1.1] h-7 w-7 flex items-center justify-center transition-all ease-in-out text-white p-1 rounded-full"
                  >
                    <IconTrash size={16} />
                  </button>
                )}
              </div>
              <div className="flex flex-col justify-between w-full">
                <p className="font-semibold mb-1">{lang.get('msg.answer')}:</p>
                <div className="text-sm font-medium">
                  <Markdown>{selectedQuestionAnswer?.answer}</Markdown>
                </div>
              </div>
            </div>

            {/* conversation messages */}
            <AISearchConversationTable snippets={selectedQuestionAnswer?.snippets || []} />
          </div>
        </div>
      )}
    </div>
  );
}

export default AISearch;
